import * as React from 'react'

import { VariantFields as TransformerVariant } from '@thg-commerce/enterprise-components/src/transformer/productOptionsTransformer'
import { useSiteDefinition } from '@thg-commerce/enterprise-core'
import {
  OutOfStockButtonStyleInterface,
  useTheme,
} from '@thg-commerce/enterprise-pages/src/Product/theme'
import { formatPrice } from '@thg-commerce/enterprise-pricing/src'
import { Button, Price } from '@thg-commerce/gravity-elements'
import { PriceTheme } from '@thg-commerce/gravity-elements/Product/types'
import {
  css,
  mq,
  spacing,
  styled,
  zIndex,
  ZIndexLevel,
} from '@thg-commerce/gravity-theme'

const StyledButton = styled(Button)<{
  outOfStockPDPButtonStyle?: OutOfStockButtonStyleInterface
  isSticky?: boolean
  priceOnSticky?: boolean
}>`
  width: 100%;
  white-space: nowrap;
  height: 48px;

  ${(props) =>
    props.isSticky &&
    props.priceOnSticky &&
    css`
      width: 167px;
      height: 44px;
    `}

  > button {
    &:disabled {
      ${(props) =>
        props?.outOfStockPDPButtonStyle?.disabled &&
        `border: ${
          props.outOfStockPDPButtonStyle.disabled.borderWidth || '1px'
        }  solid ${
          props.outOfStockPDPButtonStyle.disabled.borderColor || 'transparent'
        };
        cursor: ${props.outOfStockPDPButtonStyle.disabled.cursor || 'auto'};`}
      &:hover {
        text-decoration: ${(props) =>
          props.outOfStockPDPButtonStyle?.hover &&
          props.outOfStockPDPButtonStyle.hover.textDecoration};
      }
    }
  }
`

const Container = styled.div<{
  isSticky?: boolean
  priceOnSticky?: boolean
}>`
  width: 100%;

  ${(props) =>
    props.isSticky &&
    css`
      ${zIndex(ZIndexLevel.Base5)}
      position: fixed;
      bottom: 0;
      left: 0;
      padding: ${spacing(1)} ${spacing(2)};
      border-top: 1px solid ${(props) => props.theme.colors.palette.greys.light};
      background-color: ${(props) => props.theme.colors.palette.greys.white};
    `}
  ${(props) =>
    props.priceOnSticky &&
    css`
      display: flex;
      align-items: center;
      justify-content: space-between;
    `}
  ${(props) => mq(props.theme.breakpointUtils.map, 'sm')} {
    position: relative;
    padding: 0;
    ${zIndex(ZIndexLevel.Base)}
  }
`

interface AddToBasketProps {
  selectedVariant?: TransformerVariant
  pricing?: {
    price: string
    rrp: string
    saving?: string
  }
  maxQuantity: number
  selectedQuantity: number
  addToBasketText: string
  forwardedRef?: React.Ref<any>
  addProductToBasketHandler?: (
    event?: React.MouseEvent<Element, MouseEvent>,
  ) => void
  disabled?: boolean
  enablePreselectedProductVariant?: boolean
  isSticky?: boolean
  outOfStockPDPButtonStyle?: OutOfStockButtonStyleInterface
  priceOnSticky?: boolean
  priceTheme?: PriceTheme
  saveLabel?: string
  isPersonalisedProduct?: boolean
  offLabel?: string
}

export const AddToBasketButton = (props: AddToBasketProps) => {
  const { defaultLocale, defaultCurrency } = useSiteDefinition()

  const theme = useTheme()

  const pricing = props.pricing
    ? {
        ...props.pricing,
        saving: props.selectedVariant?.price?.saving
          ? formatPrice(
              defaultCurrency,
              props.selectedVariant.price?.saving,
              defaultLocale,
            )
          : undefined,
      }
    : { price: '0.00', rrp: '0.00', saving: '0.00' }

  return (
    <Container isSticky={props.isSticky} priceOnSticky={props.priceOnSticky}>
      {props.isSticky && props.priceOnSticky && (
        <Price
          priceTheme={props.priceTheme}
          pricing={pricing}
          savings={{ show: true }}
          savingsPercentage={{ show: true }}
          i18nText={{
            saveLabel: props.saveLabel,
            offLabel: props.offLabel,
          }}
          isSticky={props.isSticky}
        />
      )}
      <StyledButton
        isSticky={props.isSticky}
        priceOnSticky={props.priceOnSticky}
        data-testid="add-to-basket-button"
        sizing="regular"
        emphasis={
          theme.pageTheme?.addToBasket?.addToBasketButton?.emphasis || 'high'
        }
        ref={props.forwardedRef}
        disabled={
          (props.enablePreselectedProductVariant &&
            (!props.selectedVariant?.inStock || !props.selectedVariant)) ||
          props.maxQuantity < 1 ||
          props.disabled
        }
        onClick={props.addProductToBasketHandler}
        outOfStockPDPButtonStyle={props.outOfStockPDPButtonStyle}
      >
        {props.addToBasketText}
      </StyledButton>
    </Container>
  )
}
